export const MISSION_REQUIREMENT_VERSION = 1;
export const MISSION_TYPES = ['Move', 'Simple Cycle', 'Transition', 'Combination', 'Choreography', 'Position'];
export const MISSION_MODES = ['off', 'suggested', 'required'];
export const MISSION_PERSPECTIVES = ['front', 'side', 'rear', '45_degree'];

const mode = (value) => MISSION_MODES.includes(value) ? value : 'off';
const positiveIds = (value) => [...new Set((Array.isArray(value) ? value : []).map(Number).filter((id) => Number.isInteger(id) && id > 0))];
const stored = (value, fallback) => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
};

export function normalizeMissionRequirements(input = {}) {
  const gallery = input.galleryImages && typeof input.galleryImages === 'object' ? input.galleryImages : {};
  const galleryMode = mode(gallery.mode);
  const perspectivesInput = stored(input.perspectives, {});
  const perspectives = {};
  if (Array.isArray(perspectivesInput)) {
    perspectivesInput.forEach((name) => { if (MISSION_PERSPECTIVES.includes(name)) perspectives[name] = 1; });
  } else {
    MISSION_PERSPECTIVES.forEach((name) => {
      const count = Number(perspectivesInput?.[name]);
      if (Number.isInteger(count) && count > 0) perspectives[name] = Math.min(count, 10);
    });
  }
  return {
    name: mode(input.name),
    alternativeNames: mode(input.alternativeNames),
    description: mode(input.description),
    difficulty: mode(input.difficulty),
    categories: mode(input.categories),
    contentType: mode(input.contentType),
    centerGravity: mode(input.centerGravity),
    weightPlacement: mode(input.weightPlacement),
    inertia: mode(input.inertia),
    galleryImages: { mode: galleryMode, minimum: Math.max(galleryMode === 'required' ? 1 : 0, Math.min(20, Number(gallery.minimum) || 0)) },
    perspectives,
  };
}

export function requirementsFromLegacyContent(content = {}) {
  const legacyMode = (required, suggested) => required ? 'required' : suggested ? 'suggested' : 'off';
  return normalizeMissionRequirements({
    name: legacyMode(content.name_update_required, content.name_update_suggested),
    alternativeNames: legacyMode(content.require_alternative_name_submission, content.suggest_alternative_name_submission),
    description: legacyMode(content.description_required, content.description_suggested),
    difficulty: legacyMode(content.difficulty_required, content.difficulty_suggested),
    categories: legacyMode(content.category_required, content.category_suggested),
    contentType: legacyMode(content.type_required, content.type_suggested),
    centerGravity: legacyMode(content.center_gravity_required, content.center_gravity_suggested),
    weightPlacement: legacyMode(content.weight_placement_required, content.weight_placement_suggested),
    inertia: legacyMode(content.inertia_required, content.inertia_suggested),
    galleryImages: {
      mode: legacyMode(content.gallery_image_update_required, content.gallery_image_update_suggested),
      minimum: Math.max(Number(content.num_gallery_images_required) || 0, content.gallery_image_update_required ? 1 : 0),
    },
    perspectives: stored(content.required_perspectives, {}),
  });
}

export function requirementsFromCreator(requirements = {}, options = {}) {
  const legacyMode = (key) => requirements[`${key}_required`] ? 'required' : requirements[`${key}_suggested`] ? 'suggested' : 'off';
  const perspectives = {};
  (options.requiredPerspectives || []).forEach((name) => {
    if (MISSION_PERSPECTIVES.includes(name)) perspectives[name] = Math.max(1, Math.min(10, Number(options.perspectiveCounts?.[name]) || 1));
  });
  return normalizeMissionRequirements({
    name: legacyMode('name_update'),
    alternativeNames: requirements.require_alternative_name_submission ? 'required' : requirements.suggest_alternative_name_submission ? 'suggested' : 'off',
    description: legacyMode('description'), difficulty: legacyMode('difficulty'), categories: legacyMode('category'),
    contentType: legacyMode('type'), centerGravity: legacyMode('center_gravity'), weightPlacement: legacyMode('weight_placement'),
    inertia: legacyMode('inertia'),
    galleryImages: { mode: legacyMode('gallery_image_update'), minimum: Number(options.numGalleryImagesRequired) || 0 },
    perspectives,
  });
}

export function normalizeMissionSubmission(input = {}) {
  const difficulty = input.difficulty === '' || input.difficulty == null ? null : Number(input.difficulty);
  const perspectiveInput = input.perspectiveVideos && typeof input.perspectiveVideos === 'object' ? input.perspectiveVideos : {};
  const perspectiveVideos = {};
  MISSION_PERSPECTIVES.forEach((name) => {
    const values = Array.isArray(perspectiveInput[name]) ? perspectiveInput[name] : String(perspectiveInput[name] || '').split(/\r?\n/);
    perspectiveVideos[name] = values.map((value) => String(value).trim()).filter((value) => {
      try { return ['http:', 'https:'].includes(new URL(value).protocol); } catch { return false; }
    }).slice(0, 10);
  });
  return {
    proposedName: String(input.proposedName || '').trim(), proposedType: String(input.proposedType || '').trim(),
    alternativeNames: String(input.alternativeNames || '').trim(), description: String(input.description || '').trim(),
    difficulty: Number.isFinite(difficulty) ? difficulty : null, categoryIds: positiveIds(input.categoryIds),
    centerGravity: String(input.centerGravity || '').trim(), weightPlacement: String(input.weightPlacement || '').trim(),
    inertia: String(input.inertia || '').trim(), galleryImages: Array.isArray(input.galleryImages) ? input.galleryImages : [],
    perspectiveVideos,
  };
}

export function validateMissionSubmission(requirementsInput, payloadInput) {
  const requirements = normalizeMissionRequirements(requirementsInput);
  const payload = normalizeMissionSubmission(payloadInput);
  const missing = [];
  const required = (key, label, value) => { if (requirements[key] === 'required' && !value) missing.push(label); };
  required('name', 'Proposed Name', payload.proposedName);
  required('contentType', 'Proposed Content Type', payload.proposedType);
  required('alternativeNames', 'Alternative Name', payload.alternativeNames);
  required('description', 'Description', payload.description);
  required('difficulty', 'Difficulty', payload.difficulty != null);
  required('categories', 'Category', payload.categoryIds.length);
  required('centerGravity', 'Center of Gravity', payload.centerGravity);
  required('weightPlacement', 'Weight Placement', payload.weightPlacement);
  required('inertia', 'Inertia', payload.inertia);
  if (payload.proposedType && !MISSION_TYPES.includes(payload.proposedType)) missing.push('Valid Content Type');
  if (payload.difficulty != null && (!Number.isInteger(payload.difficulty) || payload.difficulty < 1 || payload.difficulty > 5)) missing.push('Difficulty must be a whole number between 1 and 5');
  if (requirements.galleryImages.mode === 'required' && payload.galleryImages.length < requirements.galleryImages.minimum) {
    missing.push(`${requirements.galleryImages.minimum} Gallery Image${requirements.galleryImages.minimum === 1 ? '' : 's'}`);
  }
  Object.entries(requirements.perspectives).forEach(([name, count]) => {
    if ((payload.perspectiveVideos[name] || []).length < count) missing.push(`${name.replace('_', ' ')} Perspective (${count})`);
  });
  return { missing, requirements, payload };
}

export function missionIsAvailable(mission, now = new Date()) {
  if (!mission || !['open', 'scheduled'].includes(mission.mission_status || mission.status)) return false;
  if ((mission.mission_status || mission.status) === 'scheduled' && mission.starts_at && new Date(mission.starts_at) > now) return false;
  if (mission.starts_at && new Date(mission.starts_at) > now) return false;
  if (mission.ends_at && new Date(mission.ends_at) <= now) return false;
  return true;
}

export function missionHasContributionWork(requirementsInput) {
  const requirements = normalizeMissionRequirements(requirementsInput);
  return ['name', 'alternativeNames', 'description', 'difficulty', 'categories', 'contentType', 'centerGravity', 'weightPlacement', 'inertia']
    .some((key) => requirements[key] !== 'off')
    || requirements.galleryImages.mode !== 'off'
    || Object.keys(requirements.perspectives).length > 0;
}
